// src/App.js
import { useEffect, useState } from "react";
import { loadSession, clearSession } from "./authStore";

import LoginPage from "./LoginPage";
import DivisionSelect from "./DivisionSelect";
import SiteSelect from "./SiteSelect";
import MainMenu from "./MainMenu";

import EZLookup from "./EZLookup";
import PartsSearch from "./PartsSearch";
import QRScannerModal from "./QRScannerModal";
import AllPartsList from "./AllPartsList";
import ReceiveParts from "./ReceiveParts";
import IssueParts from "./IssueParts";
import WorkOrders from "./WorkOrders";
import AssetInspection from "./AssetInspection";
import PrinterSettings from "./PrinterSettings";


function Shell({ children, profile, division, site, onLogout, buildStamp }) {
  const shell = {
    minHeight: "100vh",
    background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 60%)",
  };

  const topbar = {
    position: "sticky",
    top: 0,
    zIndex: 50,
    background: "rgba(255,255,255,0.9)",
    backdropFilter: "blur(8px)",
    borderBottom: "1px solid #e5e7eb",
  };

  const wrap = {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "14px 18px",
  };

  const brand = { display: "flex", alignItems: "center", gap: 10 };
  const logo = {
    width: 34,
    height: 34,
    borderRadius: 12,
    background: "linear-gradient(135deg, #2563eb, #06b6d4)",
    boxShadow: "0 6px 14px rgba(37,99,235,0.25)",
  };

  const chip = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid #e5e7eb",
    background: "#fff",
    fontSize: 12,
    color: "#374151",
    fontWeight: 700,
  };

  const subtle = { fontSize: 12, color: "#6b7280", fontWeight: 600 };

  const btn = {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    background: "white",
    cursor: "pointer",
    fontWeight: 700,
  };

  return (
    <div style={shell}>
      <div style={topbar}>
        <div style={wrap}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div style={brand}>
              <div style={logo} />
              <div>
                <div style={{ fontSize: 16, fontWeight: 900, letterSpacing: 0.2 }}>SmartSight360</div>
                <div style={subtle}>
                  {profile?.username ? `Signed in as ${profile.username}` : " "}
                </div>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
              {division && <span style={chip}>🏷️ Division: {division}</span>}
              {site && <span style={chip}>🏭 Site: {site}</span>}

              <button style={btn} onClick={onLogout} type="button">
                Logout
              </button>
            </div>
          </div>

          {/* Build stamp (temp) */}
          <div style={{ marginTop: 10, ...subtle }}>
            Build: {buildStamp}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "18px" }}>
        {children}
      </div>
    </div>
  );
}

function PageCard({ title, right, children }) {
  const card = {
    background: "white",
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    boxShadow: "0 10px 24px rgba(0,0,0,0.06)",
    padding: 18,
  };

  return (
    <div style={card}>
      {(title || right) && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
          <div style={{ fontSize: 18, fontWeight: 900 }}>{title}</div>
          <div>{right}</div>
        </div>
      )}
      {children}
    </div>
  );
}

function ActionButton({ children, onClick, variant = "secondary", disabled }) {
  const base = {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    cursor: disabled ? "not-allowed" : "pointer",
    fontWeight: 800,
    opacity: disabled ? 0.6 : 1,
  };

  const styles = {
    secondary: { background: "white" },
    primary: { background: "#2563eb", color: "white", border: "1px solid #1d4ed8" },
  };

  return (
    <button type="button" onClick={onClick} disabled={disabled} style={{ ...base, ...styles[variant] }}>
      {children}
    </button>
  );
}

export default function App() {
  const [profile, setProfile] = useState(null);
  const [division, setDivision] = useState("");
  const [site, setSite] = useState("");
  const [page, setPage] = useState("boot");
  const [scanOpen, setScanOpen] = useState(false);

  useEffect(() => {
    const sess = loadSession();
    if (sess?.profile) {
      setProfile(sess.profile);
      setPage("division");
    } else {
      setPage("login");
    }
  }, []);

  const logout = () => {
    clearSession();
    setProfile(null);
    setDivision("");
    setSite("");
    setPage("login");
  };

  const onScanResult = (text) => {
    console.log("QR scanned:", text);
  };

  const buildStamp = process.env.REACT_APP_BUILD_SHA || "no-sha";

  // login stays full-screen (no shell)
  if (page === "login") {
    return <LoginPage onLoggedIn={(p) => { setProfile(p); setPage("division"); }} />;
  }

  // Division + Site pages keep their own UI (we can polish those next)
  if (page === "division") {
    return (
      <Shell profile={profile} division={division} site={site} onLogout={logout} buildStamp={buildStamp}>
        <DivisionSelect
          onSelect={(d) => {
            setDivision(d);
            setPage("site");
          }}
        />
      </Shell>
    );
  }

  if (page === "site") {
    return (
      <Shell profile={profile} division={division} site={site} onLogout={logout} buildStamp={buildStamp}>
        <SiteSelect
          division={division}
          onBack={() => setPage("division")}
          onSelect={(s) => {
            setSite(s);
            setPage("menu");
          }}
        />
      </Shell>
    );
  }

  // everything else uses the professional shell
  return (
    <Shell profile={profile} division={division} site={site} onLogout={logout} buildStamp={buildStamp}>
      <QRScannerModal open={scanOpen} onClose={() => setScanOpen(false)} onResult={onScanResult} />

      {page === "menu" && (
        <MainMenu
          onNav={(k) => setPage(k)}
          onChangeSite={() => {
            setSite("");
            setPage("site");
          }}
        />

      )}

      {page === "ezlookup" && (
        <PageCard
          title="EZ Lookup"
          right={<ActionButton variant="primary" onClick={() => setScanOpen(true)}>Scan QR</ActionButton>}
        >
          <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
            <ActionButton onClick={() => setPage("menu")}>← Back</ActionButton>
          </div>
          <EZLookup site={site} />
        </PageCard>
      )}

      {page === "search" && (
        <PageCard
          title="Search Parts"
          right={<ActionButton variant="primary" onClick={() => setScanOpen(true)}>Scan QR</ActionButton>}
        >
          <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
            <ActionButton onClick={() => setPage("menu")}>← Back</ActionButton>
          </div>
          <PartsSearch site={site} />
        </PageCard>
      )}

      {page === "allparts" && (
        <PageCard title="All Parts" right={<ActionButton variant="primary" onClick={() => setScanOpen(true)}>Scan QR</ActionButton>}>
          <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
            <ActionButton onClick={() => setPage("menu")}>← Back</ActionButton>
          </div>
          <AllPartsList site={site} />
        </PageCard>
      )}

      {page === "issue" && (
        <PageCard title="Issue Parts">
          <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
            <ActionButton onClick={() => setPage("menu")}>← Back</ActionButton>
          </div>
          <IssueParts site={site} />
        </PageCard>
      )}

      {page === "receive" && (
        <PageCard title="Receive Parts">
          <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
            <ActionButton onClick={() => setPage("menu")}>← Back</ActionButton>
          </div>
          <ReceiveParts site={site} />
        </PageCard>
      )}

      {page === "workorders" && (
        <PageCard title="Work Orders">
          <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
            <ActionButton onClick={() => setPage("menu")}>← Back</ActionButton>
          </div>
          <WorkOrders site={site} />
        </PageCard>
      )}

      {page === "assetinspection" && (
        <PageCard title="Asset Inspection">
          <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
            <ActionButton onClick={() => setPage("menu")}>← Back</ActionButton>
          </div>
          <AssetInspection site={site} />
        </PageCard>
      )}
      {page === "printersettings" && (
        <PageCard title="Printer Settings">
          <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
            <ActionButton onClick={() => setPage("menu")}>← Back</ActionButton>
          </div>
          <PrinterSettings site={site} />
        </PageCard>
      )}
  
    </Shell>
  );
}
